import { Alert, AlertTitle, Box, Chip, Typography } from "@mui/material";
import { useRecordContext } from "react-admin";
import { DocumentRead, DocumentStatus, DocumentTransformationStatus, FailedRecord } from "@/types";
import { isFailedStatus } from "@/constants/documentStatus";
import { useDocumentTransformationData } from "../hooks/useDocumentTransformationData";
import { useDocumentLoads } from "./result-tab/hooks";

const FAILURE_CONFIG: Record<string, { title: string; fallback: string }> = {
  [DocumentStatus.UPLOAD_FAILED]: {
    title: "Upload Failed",
    fallback: "The file could not be uploaded. Please try uploading again.",
  },
  [DocumentStatus.PROCESSING_FAILED]: {
    title: "Processing Failed",
    fallback: "An error occurred while processing this document.",
  },
  [DocumentStatus.LOADING_FAILED]: {
    title: "Loading Failed",
    fallback: "Records could not be loaded into the system.",
  },
  [DocumentStatus.FAILED]: {
    title: "Failed",
    fallback: "An unexpected error occurred.",
  },
};

/** How many unique error messages to inline before truncating to a count. */
const MAX_INLINE_ERRORS = 3;

interface LoadFailureSummary {
  totalFailed: number;
  uniqueErrors: Array<{ error: string; count: number }>;
}

/**
 * Group failed_records by their error message, preserving first-seen order.
 * Identical errors collapse into one entry with an occurrence count so the
 * banner can show "12 × no Member ID" instead of repeating the message.
 *
 * Exported for unit testing — also used internally by the banner.
 */
export const summarizeLoadFailures = (failedRecords: FailedRecord[]): LoadFailureSummary => {
  const counts = new Map<string, number>();
  for (const record of failedRecords) {
    counts.set(record.error, (counts.get(record.error) ?? 0) + 1);
  }
  return {
    totalFailed: failedRecords.length,
    uniqueErrors: Array.from(counts.entries()).map(([error, count]) => ({ error, count })),
  };
};

/**
 * Banner content payload — either a single message string or a structured
 * load-failure summary that the renderer expands into a per-error list.
 */
type BannerDetails = { kind: "message"; message: string } | { kind: "loadFailure"; summary: LoadFailureSummary };

/**
 * Derive banner content from transformation or load data when the document
 * record itself doesn't carry an error_message. Load failures return a
 * structured summary so the banner can render each unique error inline,
 * rather than collapsing distinct failures behind a count.
 */
const useFailureDetails = (documentRecord: DocumentRead | undefined): BannerDetails | null => {
  const status = documentRecord?.status;
  const isFailed = isFailedStatus(status);

  const shouldFetchTransformation =
    isFailed && (status === DocumentStatus.PROCESSING_FAILED || status === DocumentStatus.FAILED);
  const shouldFetchLoad = isFailed && status === DocumentStatus.LOADING_FAILED;

  const { latestTransformation } = useDocumentTransformationData({
    documentId: shouldFetchTransformation ? documentRecord?.id : undefined,
  });

  const { latestLoad } = useDocumentLoads(shouldFetchLoad ? documentRecord?.id : undefined);

  if (!isFailed || !status) return null;

  // 1. If the document itself carries an error_message, use it
  if (documentRecord?.error_message) return { kind: "message", message: documentRecord.error_message };

  // 2. For processing failures — pull from transformation validation_errors
  if (shouldFetchTransformation && latestTransformation) {
    const transformationFailed = latestTransformation.status === DocumentTransformationStatus.FAILED;
    const errors = latestTransformation.validation_errors;

    if (transformationFailed && errors && errors.length > 0) {
      return { kind: "message", message: errors.map((e) => e.message).join("; ") };
    }

    if (transformationFailed) {
      return {
        kind: "message",
        message: FAILURE_CONFIG[status]?.fallback ?? FAILURE_CONFIG[DocumentStatus.FAILED].fallback,
      };
    }
  }

  // 3. For loading failures — return structured summary so the banner can
  //    list every distinct error. Hiding two distinct errors behind a count
  //    forced reviewers to dig through the Loaded Records tab to learn what
  //    actually failed; surface the messages directly.
  if (shouldFetchLoad && latestLoad) {
    const failedRecords = latestLoad.failed_records ?? [];
    if (failedRecords.length > 0) {
      return { kind: "loadFailure", summary: summarizeLoadFailures(failedRecords) };
    }
  }

  // 4. Fallback
  return {
    kind: "message",
    message: FAILURE_CONFIG[status]?.fallback ?? FAILURE_CONFIG[DocumentStatus.FAILED].fallback,
  };
};

/**
 * Inline list of unique load-failure errors with occurrence counts. Up to
 * MAX_INLINE_ERRORS are rendered in full; any remainder collapses into a
 * "+ N more" line that points reviewers toward the Loaded Records tab.
 */
const LoadFailureList = ({ summary }: { summary: LoadFailureSummary }) => {
  const head = summary.uniqueErrors.slice(0, MAX_INLINE_ERRORS);
  const remaining = summary.uniqueErrors.length - head.length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography variant="body2">
        {summary.totalFailed} record{summary.totalFailed !== 1 ? "s" : ""} failed to load.
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.25 }}>
        {head.map((entry, idx) => (
          <Typography key={idx} component="li" variant="body2" sx={{ wordBreak: "break-word" }}>
            {entry.count > 1 && (
              <Box component="span" sx={{ fontWeight: 600, mr: 0.75 }}>
                ×{entry.count}
              </Box>
            )}
            {entry.error}
          </Typography>
        ))}
      </Box>
      {remaining > 0 && (
        <Typography variant="caption" color="text.secondary">
          + {remaining} more distinct error{remaining !== 1 ? "s" : ""}. See the Loaded Records tab for the full list.
        </Typography>
      )}
    </Box>
  );
};

/**
 * Error banner shown on DocumentShow when the document is in a failed state.
 * Derives error details from transformation/load data when the document itself
 * doesn't carry an error_message.
 * Self-hides for non-failed statuses.
 */
export const DocumentFailureBanner = () => {
  const documentRecord = useRecordContext<DocumentRead>();
  const details = useFailureDetails(documentRecord);

  if (!documentRecord?.status || !isFailedStatus(documentRecord.status) || !details) return null;

  const config = FAILURE_CONFIG[documentRecord.status] ?? FAILURE_CONFIG[DocumentStatus.FAILED];

  return (
    <Box px={5} pb={1}>
      <Alert severity="error" sx={{ alignItems: "flex-start" }}>
        <AlertTitle>{config.title}</AlertTitle>
        {details.kind === "message" ? (
          <Typography variant="body2">{details.message}</Typography>
        ) : (
          <LoadFailureList summary={details.summary} />
        )}
        {documentRecord.error_code && (
          <Chip
            label={documentRecord.error_code}
            size="small"
            variant="outlined"
            sx={{ mt: 1, fontFamily: "monospace", fontSize: "0.75rem" }}
          />
        )}
      </Alert>
    </Box>
  );
};
